/**
 * `<LayoutBar>` — the app's top row: the layout tabs, the way into editing
 * them, and whether the workspace is saving.
 *
 *     [ Default ][ Two Charts ]  (✎)                    fixture   saved
 *
 * It replaced a title header that read "QuFolio" and then repeated the charted
 * symbol, interval and pane count — all of which the chart's own toolbar states
 * a few pixels below it. What is left is the three things nothing else says:
 * which layout you are in, how many charts are in it, and whether your last
 * change reached the server.
 *
 * **Always rendered, even with one layout.** The strip used to appear only when
 * there were two, on the grounds that a single layout needs no chooser — but the
 * row now carries the count button, which is the only way a second layout gets
 * created, and the save indicator, which is the only sign that a change did not
 * persist. There is no state in which it has nothing to say.
 *
 * The button draws a pencil. It drew the chart count until 2026-09-19, on the
 * reasoning that the count is what a user chooses between — but a bare number
 * does not read as a button, and this is the only way into layout editing. The
 * count is in its tooltip and accessible name instead.
 */

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import type { Layout, SyncFlags } from "@qufolio/model";

import type { SaveStatus } from "../api/persistence";
import { chartsOf, type ScreenerPlacement } from "../lib/layout-templates";
import { isHidden } from "../lib/layout-visibility";
import { tabStrip } from "../lib/tab-strip";
import type { LayoutSummary } from "../api/layouts";
import { LayoutPanel, type LayoutPanelProps } from "./LayoutPanel";
import { AppMenu } from "./AppMenu";
import { wsUrl } from "../api/backend";
import { fillRow, type OneRow } from "../lib/chip-rows";
import { Menu, MenuRow } from "./Menu";
import { QueVeeMark } from "./QueVeeMark";

export interface LayoutBarProps {
  layouts: readonly Layout[];
  /** Index of the layout on screen; `-1` when the workspace has none. */
  layoutIndex: number;
  saveStatus: SaveStatus;
  onSelect: (index: number) => void;
  onRename: (layoutId: string, name: string) => void;
  /** An arrangement applied — the whole layout, already rearranged. */
  onReplace: (layout: Layout) => void;
  /** Straight through to the panel's Screener row. */
  onAddScreener?: (placement: ScreenerPlacement) => void;
  onAdd: (layout: Layout) => void;
  /** Straight through to the panel; the selection is the caller's to move. */
  onSetLayoutHidden: (layoutId: string, hidden: boolean) => void;
  /** The layout row's 🔒 switch: lock or unlock every chart's symbol. */
  onSetSymbolLocked?: (layoutId: string, locked: boolean) => void;
  onRemoveLayout: (layoutId: string) => void;
  onSync: (layoutId: string, sync: SyncFlags) => void;
  /**
   * Whether the journal tab is the selected one — view state (D8), absent with
   * no journal server.
   *
   * Which tab is selected is a session fact, exactly as `layoutIndex` is. Where
   * the journal *sits* is not, and comes in below as workspace data.
   */
  showTrades?: boolean;
  /** Where the journal sits among the layouts, and whether it is put away. */
  journalIndex?: number;
  journalHidden?: boolean;
  /** A tab dragged in the panel, as two positions in `tabOrder`. */
  onMoveTab?: (from: number, to: number) => void;
  onSetJournalHidden?: (hidden: boolean) => void;
  /** Opens the theme editor — the same handler the right-click menu uses. */
  onOpenSettings: () => void;
  /** Opens the clubs-and-grants screen. `AppMenu` shows it only to an admin. */
  onOpenAdmin: () => void;
  onOpenDataHealth: () => void;
  /** Opens the read-only plan screen. Offered to everyone. */
  onOpenPlan: () => void;
  /** Opens the group request form. Passed through to the ☰ menu. */
  onOpenGroupRequest?: () => void;
  /** Opens Books — broker imports. Passed through to the ☰ menu. */
  onOpenBooks?: () => void;
  /** Replays the product tour. Absent on shells that have nothing to tour. */
  onStartTour?: () => void;
  onShowTrades?: (show: boolean) => void;
  /** Passed straight through to the panel, which is where publishing lives. */
  publishTarget?: string | null;
  onPublishLayout?: (input: { name: string; layout: Layout }) => Promise<number | undefined | void>;
  /** The publish target as the author reads it — `JP`, not `grp-jp`. */
  publishTargetName?: string | null;
  /** Re-read the listing after a Restore. */
  onPublishedChanged?: () => Promise<void>;
  /** Withdraw one the club holds — the same control, clicked again. */
  onUnpublishLayout?: (key: string) => Promise<void>;
  /** Keep the arrangement on screen as one of the caller's own templates. */
  onSaveLayoutTemplate?: (input: { name: string; layout: Layout }) => Promise<void>;
  onRemoveLayoutTemplate?: (key: string) => Promise<void>;
  layoutPresets?: readonly LayoutSummary[];
  onApplyLayoutPreset?: (key: string, name: string) => void | Promise<void>;
  /** A virtual group's inherited layouts and overrides — see `LayoutPanelProps`. */
  inheritedLayouts?: React.ReactNode;
  /** Shared with me's ● new, and Promote & Publish — see `LayoutPanelProps`. */
  sharedStates?: LayoutPanelProps["sharedStates"];
  promoteTo?: LayoutPanelProps["promoteTo"];
  onPromoteLayout?: LayoutPanelProps["onPromote"];
  onAddSection?: LayoutPanelProps["onAddSection"];
  /**
   * The panel is opening. The caller re-reads the layout listing here, so a
   * group's republish shows its new version without a page refresh
   * (`docs/plans/group-layout-sync.md`, step 2) — only on opening, never on
   * closing, because closing is not about to show anything.
   */
  onOpenPanel?: () => void;
  /**
   * Open the command search. Absent, the box is not drawn.
   *
   * A button dressed as a search box, not an input: typing happens in the
   * palette it opens, so there is one search and not two that disagree.
   */
  onOpenSearch?: () => void;
  /**
   * **A layout window** (`lib/window-sync.ts`): the strip shows this one tab
   * and nothing else — no journal, no other layouts (owner, 2026-10-28).
   */
  soleLayoutId?: string | undefined;
  /** Layouts open in windows of their own: their tab stays, marked ⧉. */
  elsewhere?: readonly string[] | undefined;
  /** Open a layout in its own window — ⧉ on its tab, on hover. */
  onDetachLayout?: ((layoutId: string) => void) | undefined;
  /** Bring a layout's window forward — what clicking its ⧉ tab means. */
  onFocusElsewhere?: ((layoutId: string) => void) | undefined;
  /**
   * Said after the current tab's name: a data layout's followed symbol,
   * `Data · AAPL` (owner, 2026-10-29) — the only place a grid with no chart on
   * screen says which company it describes.
   */
  currentNote?: string | undefined;
}

/**
 * What the save indicator says. `"off"` names the state rather than hiding it:
 * a blank corner is indistinguishable from a broken save, and running without
 * `VITE_QV_SET_URL` is the single most likely reason a change does not persist.
 */
const SAVE_LABEL: Record<SaveStatus, string> = {
  off: "not saving (no VITE_QV_SET_URL)",
  loading: "loading…",
  idle: "saved",
  saving: "saving…",
  error: "save failed — see console",
};

/**
 * Whether the charts are on a live socket or the offline fixture.
 *
 * Kept when the rest of the old header went, because "am I looking at real
 * data" is visible nowhere else — a fixture chart looks exactly like a live one
 * until you try to trade off it.
 */
// Reads the *resolved* socket rather than the build-time variable: with a
// backend override the chart is live even though VITE_QV_WS_URL is unset, and a
// badge saying "fixture" over live data is worse than no badge.
const FEED = wsUrl("/ws/qv", import.meta.env.VITE_QV_WS_URL) ? "live" : "fixture";

export function LayoutBar({
  layouts,
  layoutIndex,
  saveStatus,
  onSelect,
  onRename,
  onReplace,
  onAddScreener,
  onAdd,
  onSetLayoutHidden,
  onSetSymbolLocked,
  onRemoveLayout,
  onSync,
  showTrades,
  onShowTrades,
  journalIndex,
  journalHidden,
  onMoveTab,
  onSetJournalHidden,
  onOpenSettings,
  onOpenAdmin,
  onOpenDataHealth,
  onOpenPlan,
  onOpenGroupRequest,
  onOpenBooks,
  onStartTour,
  publishTarget,
  publishTargetName,
  onPublishedChanged,
  onPublishLayout,
  onUnpublishLayout,
  onSaveLayoutTemplate,
  onRemoveLayoutTemplate,
  layoutPresets,
  onApplyLayoutPreset,
  inheritedLayouts,
  sharedStates,
  promoteTo,
  onPromoteLayout,
  onAddSection,
  onOpenPanel,
  onOpenSearch,
  soleLayoutId,
  elsewhere = [],
  onDetachLayout,
  onFocusElsewhere,
  currentNote,
}: LayoutBarProps) {
  const [open, setOpen] = useState(false);
  const [countHover, setCountHover] = useState(false);
  const countRef = useRef<HTMLButtonElement | null>(null);

  const layout = layouts[layoutIndex];
  const count = layout ? chartsOf(layout).length : 0;
  const charts = `${count} chart${count === 1 ? "" : "s"}`;

  // ── overflow (owner, 2026-10-19) ──────────────────────────────────────────
  //
  // The tabs never wrap and never squeeze: as many as fit at full width, the
  // rest in a `▾ N` menu, and ✎ always on screen. Measured as `ScreenTabs` is —
  // against an off-screen ruler holding every tab, because the strip itself
  // holds only the ones shown last time — and fitted by `fillRow`, whose
  // two-pass rule keeps the chevron from existing only to hold the tab it
  // displaced. The current tab is required, so it takes the last slot.
  const entries = tabStrip(layouts, journalIndex).filter((tab) =>
    soleLayoutId !== undefined
      ? tab.kind !== "journal" && tab.layout.id === soleLayoutId
      : tab.kind === "journal"
        ? journalHidden !== true && onShowTrades !== undefined
        : !isHidden(tab.layout),
  );
  const [hoverTab, setHoverTab] = useState<string | null>(null);
  /** A tab's label, with ⧉ when its layout is in another window — measured that way too. */
  const tabLabel = (layout: Layout) => {
    const named = currentNote && layout.id === layouts[layoutIndex]?.id ? `${layout.name} · ${currentNote}` : layout.name;
    return elsewhere.includes(layout.id) ? `${named} ⧉` : named;
  };
  const currentEntry = entries.findIndex((tab) =>
    tab.kind === "journal" ? showTrades === true : tab.index === layoutIndex && !showTrades,
  );
  const barRef = useRef<HTMLDivElement | null>(null);
  const rulerRef = useRef<HTMLDivElement | null>(null);
  const chevronRef = useRef<HTMLButtonElement | null>(null);
  const [fit, setFit] = useState<OneRow | null>(null);
  const [overflowOpen, setOverflowOpen] = useState(false);

  const measure = useCallback(() => {
    const bar = barRef.current;
    const ruler = rulerRef.current;
    if (!bar || !ruler) return;
    const widths = Array.from(ruler.children).map((c) => (c as HTMLElement).offsetWidth);
    const budget = tabBudget(bar);
    // Zero is "no layout yet" (jsdom, or the frame before first paint), not a
    // bar of zero width: filling on it would hide every tab but the current.
    const next: OneRow | null =
      budget > 0 && widths.length > 0 && widths.every((w) => w > 0)
        ? fillRow(widths, budget, {
            gap: TAB_GAP,
            overflowWidth: chevronRef.current?.offsetWidth || CHEVRON_WIDTH,
            required: currentEntry >= 0 ? [currentEntry] : [],
          })
        : null;
    setFit((prev) => (sameFit(prev, next) ? prev : next));
  }, [currentEntry]);

  useLayoutEffect(measure);

  useEffect(() => {
    const bar = barRef.current;
    if (!bar || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => measure());
    observer.observe(bar);
    return () => observer.disconnect();
  }, [measure]);

  // A fit measured for another set of tabs is stale until the next measure.
  const fitted = fit && fit.shown.length + fit.hidden.length === entries.length ? fit : null;
  const shown = fitted ? fitted.shown : entries.map((_, i) => i);
  const hidden = fitted ? fitted.hidden : [];
  const entryName = (tab: (typeof entries)[number]) => (tab.kind === "journal" ? "Journal" : tabLabel(tab.layout));
  const choose = (tab: (typeof entries)[number]) => {
    if (tab.kind === "journal") {
      onShowTrades?.(true);
      return;
    }
    onSelect(tab.index);
    onShowTrades?.(false);
  };

  return (
    <div ref={barRef} className="qf-titlebar" style={layoutTabsStyle}>
      {/* The ruler: every tab, off-screen, never interactive — the same style
          as a real tab, or the widths are of something else. */}
      <div ref={rulerRef} aria-hidden data-bar-skip style={RULER}>
        {entries.map((tab) => (
          <span key={tab.kind === "journal" ? "journal" : tab.layout.id} style={layoutTabActiveStyle}>
            {entryName(tab)}
          </span>
        ))}
      </div>
      <AppMenu
        onOpenSettings={onOpenSettings}
        onOpenAdmin={onOpenAdmin}
        onOpenDataHealth={onOpenDataHealth}
        onOpenPlan={onOpenPlan}
        onOpenGroupRequest={onOpenGroupRequest}
        onOpenBooks={onOpenBooks}
        onStartTour={onStartTour}
      />
      {/* **A hidden tab is not drawn, and the rest keep their positions.**
          `onSelect` takes a position in `workspace.layouts`, so the filter has
          to happen after the index is read — renumbering here would send every
          click past a hidden layout to the wrong desk. `tabStrip` carries that
          true index through, and splices the journal in wherever the user
          dragged it.

          **The journal is a tab, not a mode.** It used to be a checkbox in the
          panel that swapped the charts for the journal under whichever layout
          happened to be selected; here it is a sibling of the layouts, which is
          what it always was to a reader. It has no ×: selecting a layout is how
          you leave it. */}
      <div data-bar-skip style={TABS}>
      {shown.map((at) => entries[at]!).map((tab) => {
        if (tab.kind === "journal") {
          if (!onShowTrades) return null;
          return (
            <button
              key="journal"
              type="button"
              aria-current={showTrades ? "page" : undefined}
              onClick={() => onShowTrades(true)}
              style={showTrades ? layoutTabActiveStyle : layoutTabStyle}
            >
              Journal
            </button>
          );
        }
        const current = tab.index === layoutIndex && !showTrades;
        const isElsewhere = elsewhere.includes(tab.layout.id);
        const detachable = onDetachLayout !== undefined && !isElsewhere && soleLayoutId === undefined;
        return (
          // **A layout detaches from its own tab** (owner, 2026-10-28): ⧉ over
          // the tab's corner on hover, taking no width, as on the data panel's
          // tabs. Out, the tab stays where it was, marked ⧉, and a click brings
          // its window forward instead of showing it here too.
          <span
            key={tab.layout.id}
            style={TAB_HOST}
            onPointerEnter={() => setHoverTab(tab.layout.id)}
            onPointerLeave={() => setHoverTab((t) => (t === tab.layout.id ? null : t))}
          >
          <button
            type="button"
            aria-current={current ? "page" : undefined}
            // **The product tour points at the current tab, not the strip.**
            // The strip has no element of its own — `layoutTabsStyle`'s div is
            // the whole top row, menu and status included — and a wrapper round
            // the tabs would make them one flex item instead of several, which
            // changes how they lay out. One tab is enough to say "each of these
            // is a desk". See lib/tour.ts.
            data-tour={current ? "layout-tabs" : undefined}
            // Selecting a layout leaves the journal, or the tab would light up
            // over a screen that is still the journal's.
            onClick={() => {
              if (isElsewhere) {
                onFocusElsewhere?.(tab.layout.id);
                return;
              }
              onSelect(tab.index);
              onShowTrades?.(false);
            }}
            title={isElsewhere ? `${tab.layout.name} is in its own window — show it` : undefined}
            style={current ? layoutTabActiveStyle : layoutTabStyle}
          >
            {tabLabel(tab.layout)}
          </button>
          {detachable && hoverTab === tab.layout.id && (
            <button
              type="button"
              onClick={() => onDetachLayout(tab.layout.id)}
              title={`Open ${tab.layout.name} in its own window`}
              aria-label={`Open ${tab.layout.name} in its own window`}
              style={TAB_DETACH}
            >
              ⧉
            </button>
          )}
          </span>
        );
      })}
      </div>

      {hidden.length > 0 && (
        <button
          ref={chevronRef}
          type="button"
          data-bar-skip
          aria-haspopup="menu"
          aria-expanded={overflowOpen}
          title={`${hidden.length} more`}
          onClick={() => setOverflowOpen((v) => !v)}
          style={CHEVRON}
        >
          ▾ {hidden.length}
        </button>
      )}
      {overflowOpen && hidden.length > 0 && (
        <Menu anchorRef={chevronRef} onClose={() => setOverflowOpen(false)}>
          {hidden.map((at) => {
            const tab = entries[at]!;
            return (
              <MenuRow
                key={tab.kind === "journal" ? "journal" : tab.layout.id}
                active={at === currentEntry}
                onSelect={() => {
                  choose(tab);
                  setOverflowOpen(false);
                }}
              >
                {entryName(tab)}
              </MenuRow>
            );
          })}
        </Menu>
      )}

      <button
        ref={countRef}
        type="button"
        aria-label={`Edit layouts, ${charts}`}
        // The tour's anchor: the title and label both carry the count.
        data-tour="layouts"
        aria-haspopup="dialog"
        aria-expanded={open}
        title={`Edit layouts · ${charts}`}
        onClick={() => {
          // Outside the updater: React may run an updater twice, and the
          // refetch is a side effect.
          if (!open) onOpenPanel?.();
          setOpen(!open);
        }}
        onPointerEnter={() => setCountHover(true)}
        onPointerLeave={() => setCountHover(false)}
        style={countButtonStyle(open || countHover)}
      >
        <svg
          width="12"
          height="12"
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M11.3 2.4l2.3 2.3L5.4 12.9 2.6 13.4l.5-2.8z" />
          <path d="M9.8 3.9l2.3 2.3" />
        </svg>
      </button>

      {open && (
        <LayoutPanel
          anchorRef={countRef}
          layouts={layouts}
          layoutIndex={layoutIndex}
          onSelect={onSelect}
          onRename={onRename}
          onReplace={onReplace}
          onAddScreener={onAddScreener}
          onAdd={onAdd}
          onSetHidden={onSetLayoutHidden}
          onSetSymbolLocked={onSetSymbolLocked}
          onRemove={onRemoveLayout}
          onSync={onSync}
          showTrades={showTrades}
          onShowTrades={onShowTrades}
          journalIndex={journalIndex}
          journalHidden={journalHidden}
          onMoveTab={onMoveTab}
          onSetJournalHidden={onSetJournalHidden}
          publishTarget={publishTarget}
          publishTargetName={publishTargetName}
          onPublishedChanged={onPublishedChanged}
          onPublish={onPublishLayout}
          onUnpublish={onUnpublishLayout}
          onSaveTemplate={onSaveLayoutTemplate}
          onRemoveTemplate={onRemoveLayoutTemplate}
          layoutPresets={layoutPresets}
          onApplyPreset={onApplyLayoutPreset}
          inheritedSlot={inheritedLayouts}
          sharedStates={sharedStates}
          promoteTo={promoteTo}
          onPromote={onPromoteLayout}
          onAddSection={onAddSection}
          onClose={() => setOpen(false)}
        />
      )}

      {/* `data-tour`: the tour's last stop, which is where it says there is no
          Save button. Nothing else here is a stable selector — the save word
          changes with the state. See lib/tour.ts. */}
      {onOpenSearch && (
        <button
          type="button"
          onClick={onOpenSearch}
          data-bar-search
          aria-label="Search or type a command (Ctrl+Q)"
          title="Search, type a command or ask AI (QueVee) — Ctrl+Q or Ctrl+K"
          style={SEARCH}
        >
          <QueVeeMark size={18} />
          <svg width="13" height="13" viewBox="0 0 16 16" aria-hidden="true" style={{ flex: "0 0 auto" }}>
            <circle cx="7" cy="7" r="4.5" fill="none" stroke="currentColor" strokeWidth="1.6" />
            <path d="M10.5 10.5 14 14" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
          </svg>
          <span style={SEARCH_TEXT}>Search or type a command</span>
          <kbd style={SEARCH_KEY}>Ctrl Q</kbd>
        </button>
      )}
      <div style={onOpenSearch ? STATUS : { ...STATUS, marginLeft: "auto" }} data-tour="status">
        <span title="Chart data source">{FEED}</span>
        <span
          title="Workspace persistence. Saves are debounced ~600ms after the last change."
          style={{
            color:
              saveStatus === "error" || saveStatus === "off"
                ? "var(--qf-negative)"
                : "var(--qf-text-muted)",
          }}
        >
          {SAVE_LABEL[saveStatus]}
        </span>
      </div>
    </div>
  );
}

/** The bar's flex gap, which `fillRow` must charge between tabs too. */
const TAB_GAP = 2;
/** `▾ 14`'s width before it has rendered once. */
const CHEVRON_WIDTH = 44;
/** The search box's `minWidth`: it gives way to that before any tab does. */
const SEARCH_MIN = 28;

/**
 * The width the tabs may take: the bar's content box less everything else in
 * it at its smallest — ✎, the menu, the status, and the search box at its
 * minimum, since it shrinks before a tab moves into the menu. The tab run, its
 * ruler and the chevron are `data-bar-skip` (`fillRow` reserves the chevron
 * itself); anything positioned out of the flow takes no room.
 */
function tabBudget(bar: HTMLElement): number {
  const style = getComputedStyle(bar);
  let budget = bar.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight);
  let items = 1; // the tab run
  for (const child of Array.from(bar.children) as HTMLElement[]) {
    if (child.hasAttribute("data-bar-skip")) continue;
    const cs = getComputedStyle(child);
    if (cs.position === "absolute" || cs.position === "fixed" || cs.display === "none") continue;
    items += 1;
    if (child.hasAttribute("data-bar-search")) {
      budget -= SEARCH_MIN;
      continue;
    }
    // An `auto` margin is free space, not a size: it is what the tabs fit into.
    const auto = child.style.marginLeft === "auto";
    budget -= child.offsetWidth + (auto ? 0 : parseFloat(cs.marginLeft) || 0) + (parseFloat(cs.marginRight) || 0);
  }
  return budget - TAB_GAP * (items - 1);
}

function sameFit(a: OneRow | null, b: OneRow | null): boolean {
  if (a === null || b === null) return a === b;
  return a.shown.join() === b.shown.join() && a.hidden.join() === b.hidden.join();
}

/** A tab and its hover ⧉. */
const TAB_HOST: React.CSSProperties = { position: "relative", display: "flex", flex: "0 0 auto" };

/** The hover ⧉: over the tab's top-right corner, so the strip's widths never move. */
const TAB_DETACH: React.CSSProperties = {
  position: "absolute",
  top: 2,
  right: 0,
  width: 14,
  height: 14,
  padding: 0,
  border: "none",
  borderRadius: 3,
  background: "var(--qf-surface)",
  color: "var(--qf-accent)",
  font: "inherit",
  fontSize: 10,
  lineHeight: "14px",
  cursor: "pointer",
};

const RULER: React.CSSProperties = {
  position: "absolute",
  left: -99999,
  top: 0,
  display: "flex",
  pointerEvents: "none",
  visibility: "hidden",
};

/** The tab run: one line, clipped rather than wrapped while a re-fit is due. */
const TABS: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: TAB_GAP,
  minWidth: 0,
  overflow: "hidden",
  flex: "0 1 auto",
};

const CHEVRON: React.CSSProperties = {
  font: "inherit",
  fontSize: 12,
  padding: "3px 8px",
  border: "1px solid var(--qf-border)",
  borderRadius: 4,
  background: "transparent",
  color: "var(--qf-text-secondary)",
  cursor: "pointer",
  whiteSpace: "nowrap",
  flex: "0 0 auto",
};

const layoutTabsStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: TAB_GAP,
  padding: "0 0.75rem 0 0.5rem",
  // **In the desktop app this row is the window's title bar**
  // (docs/plans/desktop-electron.md), and Windows draws ─ □ ✕ over its right
  // end. The `titlebar-area-*` variables say where that leaves room, and exist
  // only there: in a browser the fallbacks make both lines what they were.
  // `tabBudget` reads the computed padding, so the tabs give way to the buttons
  // with no special case. Dragging is `.qf-titlebar` in index.html.
  paddingRight: "calc(0.75rem + 100vw - env(titlebar-area-x, 0px) - env(titlebar-area-width, 100vw))",
  minHeight: "env(titlebar-area-height, 0px)",
  borderBottom: "1px solid var(--qf-border)",
  background: "var(--qf-surface)",
  flex: "0 0 auto",
  // The ruler is positioned against the bar.
  position: "relative",
  minWidth: 0,
};

const layoutTabStyle: React.CSSProperties = {
  font: "inherit",
  fontSize: 12,
  padding: "5px 12px",
  border: 0,
  // Transparent, with the active tab carrying the only mark — the same
  // reasoning as the symbol box: a row of filled buttons reads as a form.
  background: "transparent",
  color: "var(--qf-text-secondary)",
  borderBottom: "2px solid transparent",
  cursor: "pointer",
  // One line, at full width: a long name used to wrap to four lines.
  whiteSpace: "nowrap",
  flex: "0 0 auto",
};

const layoutTabActiveStyle: React.CSSProperties = {
  ...layoutTabStyle,
  color: "var(--qf-text)",
  borderBottom: "2px solid var(--qf-accent)",
};

/**
 * A circle with a pencil: the one way into layout editing, drawn as a control.
 *
 * It showed the chart count until 2026-09-19, styled as something that counts
 * rather than commands — and nobody read it as a button. The count is in the
 * tooltip and the accessible name now. Accent on hover as well as when open,
 * which is what says "this is clickable" on a desktop.
 */
function countButtonStyle(open: boolean): React.CSSProperties {
  return {
    width: 22,
    height: 22,
    marginLeft: 6,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontFamily: "inherit",
    fontSize: 11,
    fontVariantNumeric: "tabular-nums",
    padding: 0,
    borderRadius: "50%",
    // 1.5px in the icon's own colour: at 1px in the faint border grey the
    // circle all but vanished, and the pencil read as floating on nothing.
    borderWidth: 1.5,
    borderStyle: "solid",
    borderColor: open ? "var(--qf-accent)" : "var(--qf-text-secondary)",
    background: "transparent",
    color: open ? "var(--qf-accent)" : "var(--qf-text-secondary)",
    cursor: "pointer",
    flex: "0 0 auto",
  };
}

/**
 * The search box: pushed right, beside the status, and the first thing to give
 * way — it shrinks to the glass before a single tab does, because the tabs are
 * where you are and this is one way of going somewhere else.
 */
const SEARCH: React.CSSProperties = {
  marginLeft: "auto",
  flex: "0 1 240px",
  minWidth: 28,
  height: 22,
  display: "flex",
  alignItems: "center",
  gap: 6,
  padding: "0 7px",
  overflow: "hidden",
  font: "inherit",
  fontSize: 12,
  color: "var(--qf-text-muted)",
  background: "var(--qf-surface-alt)",
  border: "1px solid var(--qf-border)",
  borderRadius: 5,
  cursor: "text",
};

const SEARCH_TEXT: React.CSSProperties = {
  flex: 1,
  minWidth: 0,
  textAlign: "left",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

const SEARCH_KEY: React.CSSProperties = {
  flex: "0 0 auto",
  font: "inherit",
  fontSize: 10,
  padding: "0 4px",
  borderRadius: 3,
  border: "1px solid var(--qf-border)",
  color: "var(--qf-text-muted)",
};

const STATUS: React.CSSProperties = {
  // Auto only when the search box is absent; beside it, a fixed gap.
  marginLeft: 10,
  display: "flex",
  alignItems: "center",
  gap: 10,
  fontSize: "0.75rem",
  color: "var(--qf-text-muted)",
};
